/**
 * Helper date in timezone Europe/Rome (CET/CEST).
 *
 * Non usa dipendenze esterne: si basa esclusivamente su Intl.DateTimeFormat nativo.
 * Gestisce correttamente le transizioni DST (CET↔CEST, es. 29 mar e 25 ott 2026).
 *
 * Funzioni esportate:
 *  - romeMidnight(romeDateStr)      — UTC Date per la mezzanotte di un giorno Rome
 *  - getRomeDateStr(date)           — "YYYY-MM-DD" del giorno in timezone Rome
 *  - getRomeTimeStr(date)           — "HH:mm" dell'orologio Rome
 *  - getRomeHourStr(date)           — "HH:00", ora piena dell'orologio Rome
 *  - addDaysToDateStr(dateStr, n)   — aritmetica calendare su stringhe "YYYY-MM-DD"
 *  - getTodayRangeRome(now?)        — range [inizio-oggi, inizio-domani) in Rome
 *  - getThisWeekRangeRome(now?)     — range [lunedì, lunedì-prossimo) in Rome
 *  - isBirthdayThisWeek(...)        — compleanno nella settimana corrente
 */

/**
 * Dato "YYYY-MM-DD" rappresentante un giorno nel calendario Europe/Rome,
 * restituisce il Date UTC che corrisponde alla mezzanotte di quel giorno
 * in timezone Europe/Rome.
 *
 * Algoritmo:
 *  1) Crea un Date a mezzanotte UTC per quella data ("YYYY-MM-DDT00:00:00Z").
 *  2) Formatta quel Date in timezone Rome per ottenere l'ora locale (es. 02:00 se UTC+2).
 *  3) Sottrae l'offset in ms → ottiene il Date UTC = mezzanotte Rome.
 *
 * Corretto per CET (UTC+1) e CEST (UTC+2); gestisce l'edge Intl che
 * talvolta restituisce "24" invece di "00" per la mezzanotte.
 */
export function romeMidnight(romeDateStr: string): Date {
  const approx = new Date(`${romeDateStr}T00:00:00Z`);
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(approx);
  const h = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const m = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const offsetMs = (h === 24 ? 0 : h) * 3_600_000 + m * 60_000;
  return new Date(approx.getTime() - offsetMs);
}

/** Restituisce "YYYY-MM-DD" del giorno corrente in timezone Europe/Rome. */
export function getRomeDateStr(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(date);
}

/**
 * Ora dell'orologio Europe/Rome in formato "HH:mm".
 *
 * Serve sia per l'etichetta oraria che il paziente legge nel promemoria sia
 * per decidere se l'esecuzione oraria dello scheduler e' quella configurata:
 * un secondo modo di leggere l'ora Rome nel progetto sarebbe un modo in piu'
 * di divergere. La normalizzazione di "24" copre l'edge di Intl, che per la
 * mezzanotte puo' restituire 24 invece di 00.
 */
export function getRomeTimeStr(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const rawH = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const rawM = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const hh = (rawH === '24' ? '00' : rawH).padStart(2, '0');
  return `${hh}:${rawM.padStart(2, '0')}`;
}

/** Ora piena dell'orologio Europe/Rome, "HH:00". */
export function getRomeHourStr(date: Date): string {
  return `${getRomeTimeStr(date).slice(0, 2)}:00`;
}

/** Aggiunge `n` giorni a una stringa "YYYY-MM-DD" usando aritmetica calendare UTC. */
export function addDaysToDateStr(dateStr: string, n: number): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * Range del giorno odierno in timezone Europe/Rome.
 * { start: mezzanotte Rome, end: mezzanotte Rome del giorno successivo }
 */
export function getTodayRangeRome(now: Date = new Date()): { start: Date; end: Date } {
  const today = getRomeDateStr(now);
  return {
    start: romeMidnight(today),
    end: romeMidnight(addDaysToDateStr(today, 1)),
  };
}

/**
 * Range della settimana corrente lunedì-domenica in timezone Europe/Rome.
 * { start: lunedì mezzanotte Rome, end: lunedì-prossimo mezzanotte Rome }
 */
export function getThisWeekRangeRome(now: Date = new Date()): { start: Date; end: Date } {
  const today = getRomeDateStr(now);
  const dayName = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Rome',
    weekday: 'short',
  }).format(now);
  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dow = DOW.indexOf(dayName); // 0=Dom, 1=Lun, ..., 6=Sab
  const fromMonday = dow === 0 ? 6 : dow - 1; // giorni da sottrarre per trovare lun
  const monday = addDaysToDateStr(today, -fromMonday);
  return {
    start: romeMidnight(monday),
    end: romeMidnight(addDaysToDateStr(monday, 7)),
  };
}

/**
 * Restituisce true se il compleanno cade nella settimana indicata dal weekRange.
 *
 * Confronto giorno/mese, ignora l'anno.
 * I pazienti senza dataNascita o con formato non "YYYY-MM-DD" vengono esclusi.
 * I nati il 29/02 non compaiono negli anni non bisestili (comportamento voluto).
 */
export function isBirthdayThisWeek(
  dataNascita: Date | string | undefined | null,
  weekRange: { start: Date; end: Date },
): boolean {
  if (!dataNascita) return false;
  const raw =
    dataNascita instanceof Date
      ? dataNascita.toISOString().slice(0, 10)
      : String(dataNascita).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;
  const birthMD = raw.slice(5); // "MM-DD"
  const monday = getRomeDateStr(weekRange.start);
  for (let i = 0; i < 7; i++) {
    if (addDaysToDateStr(monday, i).slice(5) === birthMD) return true;
  }
  return false;
}
